import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "../../../types/state/AppState";
import { isRequesting } from "../../../utils";
import { SORT, SORTING_TYPE, issuanceMonitorWidget } from "../../../constants";
import { Table } from "../../bidding/common/table";
import { topStaticDealColumns } from "./columns/TopStaticDealColumns";
import { Pagination } from "../tables/Pagination";
import {
    DashboardWidget,
    DashboardWidgetContent,
    DashboardWidgetHeader,
} from "../widget";
import { DashboardSkeleton } from "../DashboardSkeleton";
import { dashboardExportTopStaticDeals, dashboardFetchTopStaticDeals } from "../../../actions/dashboard.actions";
import { getAvailableYears, getSortingParamsForStatisticExport } from "./top-list.utils";
import { YearSelector } from "../../controls/YearSelector";
import { DashboardNoSelectedFilters } from "../DashboardNoSelectedFilters";
import { useSortedList } from "../../../effects";
import { ShowFor } from "../../access";
import ImExportButton from "../../amrPipeline/common/ImExportButton";
import { SubscriptionFeature } from '../../../types/billing/SubscriptionFeature';
import { hasWidgetsAccess } from '../../../utils/amr-pipeline.utils';
import moment from 'moment';
import { ChartRequiredFeature } from "../../access/ChartRequiredFeature";
import { TopDeal } from "../../../types/amr-pipeline/models/TopDeal";

const availableYears: number[] = getAvailableYears(issuanceMonitorWidget.dealsMinYear);

interface Props {
    onListItemClick?: () => void;
}

export function TopStaticDeals({ onListItemClick }: Props) {
    const [year, setYear] = useState<number | undefined>(availableYears[0]);
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState("");
    const [sortDirection, setSortDirection] = useState(SORT.ASC);

    const topStaticDeals = useSelector((s: AppState) => s.dashboard.topStaticDeals);
    const requestStateFetchTopStaticDeals = useSelector((s: AppState) => s.dashboard.requestStateFetchTopStaticDeals);
    const requestStateExportStaticDeals = useSelector(
        (s: AppState) => s.dashboard.requestStateExportStaticDeals
    );

    const hasAccess = hasWidgetsAccess(SubscriptionFeature.StaticDealsTable);

    const dispatch = useDispatch();

    useEffect(() => {
        hasAccess && dispatch(dashboardFetchTopStaticDeals(moment().year()));
    }, [dispatch, hasAccess]);

    const sortingType = sortBy === "currency" ? SORTING_TYPE.transactionCurrency : '';
    const [sortedDataItems] = useSortedList(topStaticDeals, sortBy, sortDirection, sortingType);

    const columnContext = () => {
        return {
            onListItemClick: onListItemClick,
        };
    };

    const handleSortChange = (field: string, direction: string) => {
        setSortBy(field);
        setSortDirection(direction);
    };

    const handleYearChange = (yearValue?: number) => {
        setYear(yearValue);
        dispatch(dashboardFetchTopStaticDeals(yearValue));
    };

    const handleExportChart = () =>
        dispatch(
            dashboardExportTopStaticDeals(
                year,
                getSortingParamsForStatisticExport(sortBy, sortDirection),
            )
        );

    const onPageChanged = (selectedPage: number) => setPage(selectedPage);

    const renderTable = () => {
        const getTableWithData = (items: TopDeal[]) => (
            <Table
                onSort={handleSortChange}
                defaultSortBy={sortBy}
                defaultSortByDirection={sortDirection}
                dataItems={items}
                columns={topStaticDealColumns}
                createSecurityCustomArgs={columnContext}
                className="data-list-striped"
            />
        );

        return sortedDataItems.length > issuanceMonitorWidget.itemsOnPage ? (
            <Pagination
                list={sortedDataItems}
                itemsOnPage={issuanceMonitorWidget.itemsOnPage}
                currentPage={page}
                onPageChanged={onPageChanged}
            >
                {(items: TopDeal[]) => getTableWithData(items)}
            </Pagination>
        ) : getTableWithData(sortedDataItems);
    }

    const chartName = "Static Deals";

    return (
        <DashboardWidget title={chartName} className="static-deals">
            <DashboardSkeleton inProgress={isRequesting(requestStateFetchTopStaticDeals)}>
                <ChartRequiredFeature
                    feature={SubscriptionFeature.StaticDealsTable}
                    chartName={chartName}
                    blockedClassName="restricted-placeholder-static-deals"
                >
                    <DashboardWidgetHeader>
                        <h3>{chartName}</h3>
                        <ShowFor feature={SubscriptionFeature.StaticDealsTable}>
                            <div className="flex-item-right">
                                <ImExportButton
                                    isExportLoad={isRequesting(requestStateExportStaticDeals)}
                                    onExport={handleExportChart}
                                    disabled={!topStaticDeals.length}
                                />
                            </div>
                            <div className="flex-item-right control-box">
                                <div className="control-item">
                                    <YearSelector
                                        years={availableYears}
                                        selectedYear={year}
                                        currentYearPrefix="YTD"
                                        onChange={handleYearChange}
                                        prefix="Year: "
                                        showAllOption
                                    />
                                </div>
                            </div>
                        </ShowFor>
                    </DashboardWidgetHeader>
                    <DashboardWidgetContent
                        requestState={requestStateFetchTopStaticDeals}
                        description={
                            sortedDataItems?.length
                                ? "All statistics are calculated based on the transaction pricing date."
                                : ""
                        }
                    >
                        {sortedDataItems.length ? renderTable() : <DashboardNoSelectedFilters />}
                    </DashboardWidgetContent>
                </ChartRequiredFeature>
            </DashboardSkeleton>
        </DashboardWidget>
    );
}
